import React, { useState, useEffect, useMemo } from 'react';
import AchievementSelectorItem from './AchievementSelectorItem';
import './AchievementSelectorModal.scss';

const AchievementSelectorModal = ({ 
  isOpen, 
  onClose, 
  onSelectAchievement, 
  availableAchievements, 
  loading 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedCategory('all');
    }
  }, [isOpen]);

  // Helper function for sorting by rarity
  const getRarityForSorting = (achievement) => {
    if (achievement.category === 'user-groups') return 'legendary';
    if (achievement.threshold >= 100) return 'epic';
    if (achievement.threshold >= 25) return 'rare';
    return 'common';
  };

  // Define categories
  const categories = [
    { id: 'all', label: 'All', count: availableAchievements.length },
    { 
      id: 'daily-streak', 
      label: 'Daily Streak', 
      count: availableAchievements.filter(a => a.category === 'daily-streak').length 
    },
    { 
      id: 'total-wins', 
      label: 'Total Wins', 
      count: availableAchievements.filter(a => a.category === 'total-wins').length 
    },
    { 
      id: 'user-groups', 
      label: 'User Groups', 
      count: availableAchievements.filter(a => a.category === 'user-groups').length 
    },
    { 
      id: 'other', 
      label: 'Other', 
      count: availableAchievements.filter(a => a.category === 'other').length 
    }
  ];

  // Filter achievements based on search term and category
  const filteredAchievements = useMemo(() => {
    let filtered = availableAchievements;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(achievement => achievement.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(achievement => 
        achievement.name.toLowerCase().includes(searchLower) ||
        achievement.description.toLowerCase().includes(searchLower)
      );
    }

    // Sort by rarity for better visual organization
    const rarityOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };
    return filtered.sort((a, b) => {
      const rarityA = getRarityForSorting(a);
      const rarityB = getRarityForSorting(b);
      return (rarityOrder[rarityA] || 4) - (rarityOrder[rarityB] || 4);
    });
  }, [availableAchievements, selectedCategory, searchTerm]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="achievement-selector-modal-overlay" 
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="achievement-selector-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="achievement-selector-modal__header">
          <h2 className="achievement-selector-modal__title">Select Achievement to Test</h2>
          <button 
            className="achievement-selector-modal__close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Search Bar */}
        <div className="achievement-selector-modal__search">
          <input
            type="text"
            placeholder="Search achievements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="achievement-selector-modal__search-input"
          />
        </div>

        {/* Category Tabs */}
        <div className="achievement-selector-modal__categories">
          {categories.map(category => (
            <button
              key={category.id}
              className={`achievement-selector-modal__category-tab ${
                selectedCategory === category.id ? 'achievement-selector-modal__category-tab--active' : ''
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.label}
              <span className="achievement-selector-modal__category-count">({category.count})</span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="achievement-selector-modal__content">
          {loading ? (
            <div className="achievement-selector-modal__loading">
              <div className="spinner"></div>
              <p>Loading achievements...</p>
            </div>
          ) : filteredAchievements.length > 0 ? (
            <div className="achievement-selector-modal__grid">
              {filteredAchievements.map(achievement => (
                <AchievementSelectorItem
                  key={achievement.id}
                  achievement={achievement}
                  onSelect={onSelectAchievement}
                />
              ))}
            </div>
          ) : (
            <div className="achievement-selector-modal__empty">
              <p>No achievements found matching your criteria.</p>
              {searchTerm && (
                <button 
                  className="achievement-selector-modal__clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="achievement-selector-modal__footer">
          <button 
            className="achievement-selector-modal__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <div className="achievement-selector-modal__help-text">
            Click any achievement to test its toast notification
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementSelectorModal;